const PLACEHOLDER_URLS = new Set(['', 'https://your-project-ref.supabase.co']);
const PLACEHOLDER_KEYS = new Set(['', 'your-anon-key', 'your-publishable-key']);

export interface SupabasePublicConfig {
  url: string;
  key: string;
  keySource: 'publishable' | 'anon';
}

export interface SupabaseEnv {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_PUBLISHABLE_KEY?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_USE_CLOUD?: string;
}

export interface SupabaseMode {
  /** True only for a production build (`vite build`, Cloudflare Pages). */
  prod: boolean;
}

/**
 * Production builds talk to Supabase when a public URL and key are set.
 * Dev and preview stay on the local save unless VITE_USE_CLOUD is exactly `1`.
 */
export function cloudAllowed(env: Pick<SupabaseEnv, 'VITE_USE_CLOUD'>, mode: SupabaseMode): boolean {
  if (mode.prod) return true;
  return env.VITE_USE_CLOUD === '1';
}

/**
 * Public client settings. The publishable key wins. The anon key is only a fallback.
 * A service-role JWT is refused. This module never reads a service-role variable.
 * The mode defaults to production so a caller that only checks key shape keeps
 * the Pages behavior. Dev passes `{ prod: false }`.
 */
export function resolveSupabasePublicConfig(
  env: SupabaseEnv,
  mode: SupabaseMode = { prod: true },
): SupabasePublicConfig | null {
  if (!cloudAllowed(env, mode)) return null;
  const url = env.VITE_SUPABASE_URL?.trim() ?? '';
  const publishable = env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() ?? '';
  const anon = env.VITE_SUPABASE_ANON_KEY?.trim() ?? '';
  const key = publishable || anon;
  if (PLACEHOLDER_URLS.has(url) || PLACEHOLDER_KEYS.has(key)) return null;
  if (isServiceRoleKey(key)) return null;
  return { url, key, keySource: publishable ? 'publishable' : 'anon' };
}

/** Runtime client. A Pages preview bundle forces this to null. */
export function publicSupabaseConfig(): SupabasePublicConfig | null {
  if (!remoteBuildEnabled()) return null;
  return resolveSupabasePublicConfig(
    {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
      VITE_USE_CLOUD: import.meta.env.VITE_USE_CLOUD,
    },
    { prod: import.meta.env.MODE === 'production' },
  );
}

/** False on a Pages preview bundle. An undeclared flag keeps the mode check. */
function remoteBuildEnabled(): boolean {
  try {
    if (typeof __THREADBOUND_REMOTE__ === 'boolean') return __THREADBOUND_REMOTE__;
  } catch {
    // A bundler that throws on the missing flag should still allow production.
  }
  return true;
}

function isServiceRoleKey(key: string): boolean {
  const payload = key.split('.')[1];
  if (!payload) return false;
  try {
    const json = JSON.parse(decodeBase64Url(payload)) as { role?: string };
    return json.role === 'service_role';
  } catch {
    return false;
  }
}

function decodeBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  return globalThis.atob(padded);
}
